import { Camera } from "@mediapipe/camera_utils";
import { FaceDetection } from "@mediapipe/face_detection";
import { Button, message, Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { getData } from "../../../../actions/apiCall";
import { decryptData, encryptData } from "../../../../actions/crypto";
import { postData, putData } from "../../../../actions/developer";

interface ScreenShareProps {
    children: React.ReactNode;
    onFaceResult?: (result: { faceDetected: boolean; singleFace: boolean }) => void;
    onCameraStart?: (status: boolean) => void;
    reload?: boolean;
}

const ScreenShare: React.FC<ScreenShareProps> = ({ children, onFaceResult, onCameraStart, reload }) => {
    const { id } = useParams();
    const [face, setFace] = useState({
        faceDetected: false,
        singleFace: false,
    });
    const [screenSharing, setScreenSharing] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isCameraChecking, setIsCameraChecking] = useState(false);
    const [isExamGoingOn, setIsExamGoingOn] = useState(false);
    const [isCameraError, setIsCameraError] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);

    const screenRecorderRef = useRef(null);
    const cameraStreamRef = useRef(null);
    const [recording, setRecording] = useState(false);
    const screenStreamRef = useRef(null);
    const cameraVideoRef = useRef<HTMLVideoElement>(null);
    const faceDetector = useRef<FaceDetection | null>(null);

    const [config, setConfig] = useState({
        screenShareStop: 0,
        cameraStop: 0,
        faceDetectionStop: 0,
        multiFaceDetected: 0,
        tabChanged: 0,
        inspectElement: 0,
        fullScreen: 0
    })

    const dispatch = useDispatch();

    useEffect(() => {
        getConfig()
    }, []);

    const getStatus = () => {
        return location.href.includes("/exam")
    }

    useEffect(() => {
        if (getStatus()) {
            if (config.screenShareStop >= 3) {
                handleSubmitTest('Screen share stopped 3 times');
            } else if (config.cameraStop >= 3) {
                handleSubmitTest('Camera stopped 3 times');
            } else if (config.faceDetectionStop >= 3) {
                handleSubmitTest('Face detection stopped 3 times');
            } else if (config.multiFaceDetected >= 3) {
                handleSubmitTest('Multiple faces detected 3 times');
            } else if (config.inspectElement >= 3) {
                handleSubmitTest('Inspect element detected 3 times');
            } else if (config.fullScreen >= 3) {
                handleSubmitTest('Full screen exit detected 3 times');
            }
        }
    }, [config, isExamGoingOn, getStatus(), location.href]);

    const getMessage = (type) => {
        const message = {
            screenShareStop: 'Screen sharing stopped. This may be considered cheating.',
            cameraStop: 'Camera stopped. This may be considered cheating.',
            faceDetectionStop: 'Face detection stopped. This may be considered cheating.',
            multiFaceDetected: 'Multiple faces detected. This may be considered cheating.',
            inspectElement: 'Inspect element detected. This may be considered cheating.',
            tabChanged: 'Tab change detected. This may be considered cheating.',
            fullScreen: 'Full screen exit detected. This may be considered cheating.',
        }
        return message[type];
    }

    const handleConfig = (type: string) => {
        if (getStatus()) {
            message.warning(getMessage(type));
            const data = encryptData({ field: type });
            const resData = dispatch(putData(`/accel-test/update/cheating/config/${id}`, { data }), false);
            if (resData) {
                getConfig()
            }
        }
    }

    const getConfig = async () => {
        try {
            const resData = await dispatch(getData(`/accel-test/fetch/cheating/config/${id}`));
            if (resData.code == 200) {
                const config = decryptData(resData.data);
                if (config) {
                    const {
                        screenShareStop,
                        cameraStop,
                        faceDetectionStop,
                        multiFaceDetected,
                        tabChanged,
                        inspectElement,
                        fullScreen
                    } = config;
                    setConfig({
                        screenShareStop,
                        cameraStop,
                        faceDetectionStop,
                        multiFaceDetected,
                        tabChanged,
                        inspectElement,
                        fullScreen
                    });
                }
            } else {
                message.error("Failed to fetch configuration.");
            }
        } catch (error) {
            message.error("Failed to fetch configuration.");
        }
    };

    useEffect(() => {
        if (location.href.includes("/exam")) {
            setIsExamGoingOn(true);
        } else {
            setIsExamGoingOn(false);
        }
    }, [location.href]);

    useEffect(() => {
        startUserView();
        startScreenShare();
    }, []);

    useEffect(() => {
        if (onFaceResult) {
            onFaceResult(face);
        }
    }, [face]);

    useEffect(() => {
        if (reload) {
            location.reload();
        }
    }, [reload]);

    useEffect(() => {
        if (getStatus() && screenSharing && !recording) {
            scheduleRandomScreenshot();
        }
    }, [isExamGoingOn, screenSharing, getStatus(), location.href]);

    useEffect(() => {
        if (screenStreamRef.current) {
            setScreenSharing(true);
        } else {
            setScreenSharing(false);
        }
    }, [screenStreamRef.current]);

    const handleSubmitTest = async (msg) => {
        try {
            const log = msg;
            const resData = await dispatch(postData(`/accel-test/faild/test/submit/${id}`, { data: encryptData({ log }) }));
            if (resData.code == 200) {
                message.success("Test submitted successfully.");
            } else {
                message.error("Failed to submit the test.");
            }
        } catch (error) {
            console.error("Error submitting test:", error);
            message.error("Failed to submit the test.");
        } finally {
            location.href = `/instructions/${id}`;
        }
    }

    const startScreenShare = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: "monitor",
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 10, max: 15 }
                },
                audio: false,
            });
            const track = stream.getVideoTracks()[0];
            screenStreamRef.current = stream;

            if (track.getSettings().displaySurface !== "monitor") {
                stream.getTracks().forEach((track) => track.stop());
                setScreenSharing(false);
                handleScreenShareStop();
                return;
            }

            track.addEventListener("ended", () => {
                setScreenSharing(false);
                handleScreenShareStop();
            });

            if (stream.active) {
                setScreenSharing(true);
                handleConfig('screenShareStop');
            }

        } catch (error) {
            console.error("Screen sharing failed", error);
            setScreenSharing(false);
            handleScreenShareStop();
        }
    };

    const handleScreenShareStop = () => {
        startScreenShare();
    };

    async function getStableCombinedStream(
        retryLimit = 50,
        retryInterval = 5000
    ) {
        let proceessing = false;
        async function getMicStream() {
            return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        }

        async function waitForValidStream(ref, type, retries = retryLimit) {
            return new Promise((resolve, reject) => {
                const tryStream = () => {
                    const stream = ref.current;
                    if (stream && stream.getTracks().length > 0) {
                        resolve(stream);
                    } else if (retries <= 0) {
                        reject(new Error(`${type} stream failed after retries.`));
                    } else {
                        --retries;
                        setTimeout(() => tryStream(), retryInterval);
                    }
                };
                tryStream();
            });
        }

        // Initial stream setup
        const micStream = await getMicStream();
        const screenStream: any = await waitForValidStream(screenStreamRef, "screen");
        const cameraStream: any = await waitForValidStream(cameraStreamRef, "camera");


        const screenVideo = document.createElement("video");
        const cameraVideo = document.createElement("video");
        screenVideo.srcObject = screenStream;
        cameraVideo.srcObject = cameraStream;

        screenVideo.muted = true;
        cameraVideo.muted = true;

        await screenVideo.play();
        await cameraVideo.play();

        const canvas = document.createElement("canvas");
        const screenTrack = screenStream.getVideoTracks()[0];
        const settings = screenTrack.getSettings();
        const width = settings.width || 1280;
        const height = settings.height || 720;

        canvas.width = width * 2;
        canvas.height = height;

        const ctx = canvas.getContext("2d")!;

        const draw = () => {
            ctx.drawImage(screenVideo, 0, 0, width, height);
            ctx.drawImage(cameraVideo, width, 0, width, height);
            requestAnimationFrame(draw);
        };
        draw();

        const combinedStream = canvas.captureStream(30); // 30 FPS
        const audioTrack = micStream.getAudioTracks()[0];
        combinedStream.addTrack(audioTrack);

        const handleTrackEnd = (label) => () => {
            if (proceessing) return;
            proceessing = true;
            startRecording()
        };

        [...screenStreamRef.current.getTracks()].forEach(track =>
            track.addEventListener("ended", handleTrackEnd("screen"))
        );
        [...cameraStreamRef.current.getTracks()].forEach(track =>
            track.addEventListener("ended", handleTrackEnd("camera"))
        );
        [...micStream.getTracks()].forEach(track =>
            track.addEventListener("ended", handleTrackEnd("mic"))
        );

        return combinedStream;
    }

    //video Recording
    const startRecording = async () => {
        try {
            let blobSize = 0;
            let blobs = [];

            const combineStream = await getStableCombinedStream()
            console.log("Combined Stream started");

            const recorder = new MediaRecorder(combineStream, {
                mimeType: "video/webm; codecs=vp9",
                videoBitsPerSecond: 200000,
            });

            recorder.ondataavailable = async (event) => {
                if (event.data.size > 0) {
                    blobSize += event.data.size;
                    blobs.push(event.data);
                    console.log("Blob size:", blobSize / 1048576);
                    if (blobSize >= 1 * 1024 * 1024) { // 3MB
                        recorder.stop();
                    }
                }
            };

            recorder.onstop = async () => {
                if (blobs.length > 0) {
                    const fullBlob = new Blob(blobs, { type: "video/webm" });
                    await sendChunkToServer(fullBlob);
                    blobs = [];
                }
                startRecording();
            };

            screenRecorderRef.current = recorder;
            recorder.start(1000);
        } catch (error) {
            console.error("Error during recording:", error);
        }
    };

    const captureScreenshot = async () => {
        try {
            const video = document.createElement("video");
            video.srcObject = screenStreamRef.current;
            video.play();

            await new Promise((resolve) => (video.onloadedmetadata = resolve));

            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext("2d");
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(async (blob) => {
                if (blob) {
                    await sendChunkToServer(blob);
                }
            }, "image/png");
        } catch (error) {
            console.error("Error capturing screenshot:", error);
        }
    };

    const scheduleRandomScreenshot = () => {
        const randomDelay = Math.floor(Math.random() * (120000 - 10000) + 10000);
        setRecording(true);
        setTimeout(() => {
            if (!screenSharing) return;
            captureScreenshot();
            scheduleRandomScreenshot();
        }, randomDelay);
    };

    const singleFaceTimeoutRef = useRef(null);
    const multiFaceTimeoutRef = useRef(null);
    const SINGLE_FACE_THRESHOLD = 30000; // 30 seconds
    const MULTI_FACE_THRESHOLD = 30000;

    const setupFaceDetection = async () => {
        try {
            if (faceDetector.current) {
                await faceDetector.current.close();
                faceDetector.current = null;
                delete faceDetector.current;
            }

            if (cameraVideoRef.current) {
                const camera = new Camera(cameraVideoRef.current, {
                    onFrame: async () => {
                        if (faceDetector.current) {
                            try {
                                await faceDetector.current.send({ image: cameraVideoRef.current as HTMLVideoElement });
                            } catch (error) {
                                console.error("Face Detection Error:", error);
                            }
                        }
                    },
                    width: 640,
                    height: 480,
                });
                camera.start();
            }

            faceDetector.current = new FaceDetection({
                locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
            });

            await faceDetector.current.setOptions({ model: "short", minDetectionConfidence: 0.5 });

            faceDetector.current.onResults((results) => {
                if (results && results.detections) {
                    const detections = results.detections;
                    const faceDetected = detections && detections.length > 0;
                    const singleFace = detections && detections.length === 1;

                    setFace((prevState) => {
                        if (prevState.faceDetected === faceDetected && prevState.singleFace === singleFace) {
                            return prevState;
                        }
                        return { faceDetected, singleFace };
                    });

                    if (!faceDetected || !singleFace) {
                        if (!singleFaceTimeoutRef.current) {
                            singleFaceTimeoutRef.current = setTimeout(() => {
                                handleConfig('faceDetectionStop');
                                singleFaceTimeoutRef.current = null;
                            }, SINGLE_FACE_THRESHOLD);
                        }
                    } else {
                        clearTimeout(singleFaceTimeoutRef.current);
                        singleFaceTimeoutRef.current = null;
                    }

                    // Handle multiple faces detected
                    if (detections.length > 1) {
                        if (!multiFaceTimeoutRef.current) {
                            multiFaceTimeoutRef.current = setTimeout(() => {
                                handleConfig('multiFaceDetected');
                                multiFaceTimeoutRef.current = null;
                            }, MULTI_FACE_THRESHOLD);
                        }
                    } else {
                        clearTimeout(multiFaceTimeoutRef.current);
                        multiFaceTimeoutRef.current = null;
                    }
                }
            });
        } catch (error) {
            console.error("Failed to initialize face detection:", error);
            setFace({
                faceDetected: false,
                singleFace: false,
            });
        }
    };

    const sendChunkToServer = async (blob) => {
        const formData = new FormData();
        formData.append("chunk", blob);
        try {
            const response = await fetch(`${process.env.API_URL}/upload-chunk/${id}`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) throw new Error("Upload failed");
        } catch (error) {
            console.error("Error uploading chunk:", error);
        }
    };

    //camera 

    useEffect(() => {
        isCameraOn(isCameraActive);
        if (isExamGoingOn) {
            if (isCameraError) {
                setIsModalVisible(true);
            } else {
                setIsModalVisible(false);
            }
        }
    }, [isCameraError, isExamGoingOn, isCameraActive])

    // Also notify when camera status changes (for system-check page)
    useEffect(() => {
        if (onCameraStart) {
            onCameraStart(isCameraActive && !isCameraError);
        }
    }, [isCameraActive, isCameraError, onCameraStart])

    const isCameraOn = (status: boolean) => {
        if (onCameraStart) {
            onCameraStart(status);
        }
    }

    const checkCameraPermissions = async () => {
        try {
            const permissions = await navigator.permissions.query({ name: "camera" as PermissionName });
            if (permissions.state === "denied") {
                console.error("Camera access is denied. Please grant permissions.");
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error checking camera permissions:", error);
            return false;
        }
    };

    const stopExistingStreams = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === "videoinput");

            if (videoDevices.length === 0) {
                console.error("No camera devices found.");
                return false;
            }

            for (const device of videoDevices) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId } });
                stream.getTracks().forEach(track => track.stop());
            }

            return true;
        } catch (error) {
            console.error("Error stopping existing streams:", error);
            return false;
        }
    };


    const startUserView = async () => {
        setIsCameraActive(false);
        setIsCameraChecking(true);

        try {
            const hasPermission = await checkCameraPermissions();
            if (!hasPermission) {
                setIsCameraError(true);
                return;
            }

            await stopExistingStreams();

            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 10, max: 15 }
                }, audio: true
            };
            const cameraStream = await navigator.mediaDevices.getUserMedia(constraints);

            if (!cameraStream || cameraStream.getVideoTracks().length === 0) {
                console.error("Camera is not accessible. Possible hardware issue.");
                setIsCameraError(true);
                return;
            }

            cameraStreamRef.current = cameraStream;

            if (cameraVideoRef.current) {
                cameraVideoRef.current.srcObject = cameraStream;
                await cameraVideoRef.current.play();
                // startRecording();
                setupFaceDetection();
                setIsCameraActive(true);
                setIsCameraError(false);
            }
        } catch (error) {
            console.error("Error accessing user camera:", error);
            setIsCameraError(true);
        } finally {
            setIsCameraChecking(false);
        }
    };

    return <div className="no-select">
        <div className="absolute bottom-4 left-4 w-40">
            <video ref={cameraVideoRef} autoPlay muted className="video-play w-full h-auto rounded-lg shadow-lg" ></video>
        </div>
        <Modal
            title="Camera Access Required"
            open={isModalVisible}
            footer={[
                <Button type="primary" onClick={startUserView} loading={isCameraChecking}>
                    Try Again
                </Button>
            ]} // No close button in footer
            closable={false} // No "X" button
            maskClosable={false} // Prevent closing on clicking outside
        >
            <p className="pb-2">
                Your camera is off. A camera is required to proceed. Please check your camera settings and try again.
            </p>
            <p className="pb-2">
                However, your test time will continue, and the test will be submitted on time. Please try to resolve the issue as soon as possible.
            </p>
            <p>
                If you believe this issue is caused by our exam portal, please record a video on your phone and share it with <span className="text-blue-500 hover:text-blue-700 transition duration-200">hr@accelgrowthtech.com</span></p>
        </Modal>
        {children}
    </div >
};

export default ScreenShare;

